/**
 * Staff data access for the AAS Solutions direct-book tracker (sol_submissions).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { SolRow } from '../types';

const ALPHABET = '0123456789';
function newSolId(): string {
  let s = '';
  for (let i = 0; i < 4; i++) s += ALPHABET[Math.floor(Math.random() * 10)];
  return 'AS-' + s;
}

export function useSolutions() {
  return useQuery<SolRow[]>({
    queryKey: ['solutions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sol_submissions')
        .select('id, created_at, stage, customer_name, data')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as SolRow[];
    },
  });
}

export function useCreateSol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { customerName: string; stage?: string; fields?: Record<string, unknown> }) => {
      const id = newSolId();
      const stage = input.stage || 'new';
      const { error } = await supabase.from('sol_submissions').insert({
        id,
        stage,
        customer_name: input.customerName,
        data: { id, customerName: input.customerName, createdAt: Date.now(), stage, ...(input.fields ?? {}) },
      });
      if (error) throw error;
      return id;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['solutions'] }),
  });
}

export function useUpdateSol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; stage?: string; customerName?: string; data: Record<string, unknown> }) => {
      const patch: Record<string, unknown> = { data: input.data };
      if (input.stage) patch.stage = input.stage;
      if (input.customerName != null) patch.customer_name = input.customerName;
      const { error } = await supabase.from('sol_submissions').update(patch).eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['solutions'] }),
  });
}

export function useDeleteSol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('sol_submissions').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['solutions'] }),
  });
}
